/* eslint-disable camelcase */
import type {
  OrderCompletedOnlinePayload,
  OrderCompletedOnlineProducts
} from '@/Infrastructure/Utils/analytics/braze/braze.types';
import { getOrderCompletedOnlineProduct } from '@/Infrastructure/Utils/analytics/braze/brazeProductTransformer.ts';
import type { Cart } from '@/modules/cart/domain/Cart';
import { SHIPPING_METHOD_TYPE } from '@/modules/perseo-billing/constants/src';
import type {
  IBasketSummary,
  IOrderConfirmation
} from '@/modules/perseo-billing/interfaces/src/bff-web/billing';

export const hasPickUpInBaskets = (basketSummary: IBasketSummary[]) => {
  return basketSummary?.some(
    (basket) =>
      basket?.deliveryDetails?.shippingMethodType ===
      SHIPPING_METHOD_TYPE.PICK_UP
  );
};

const checkFreeDelivery = (totalDeliveryPrice: string) => {
  return (
    !totalDeliveryPrice ||
    totalDeliveryPrice === '$0' ||
    totalDeliveryPrice === ''
  );
};

const getTotalOfItems = (basketSummary: IBasketSummary[]) => {
  return (
    basketSummary?.reduce((total, basket) => {
      return (
        total +
        basket.items.reduce(
          (itemTotal, item) => itemTotal + (item.quantity ?? 1),
          0
        )
      );
    }, 0) ?? 0
  );
};

const formatToNumber = (value: string | number): number => {
  return Number(value.toString().replace(/[^0-9.-]+/g, ''));
};

export const getOrderCompletedOnlinePayload = (
  cart: Cart,
  paymentSuccess: IOrderConfirmation
) => {
  const basketSummary = paymentSuccess?.basketSummary;
  const totalSummary = paymentSuccess?.totalSummary;
  const summary = totalSummary?.summary;
  const isCencoPay = paymentSuccess?.isCencoPay ?? false;

  const totalOrderPrice = summary?.unformattedTotalPrice ?? 0;
  const totalDeliveryPrice = summary?.totalDeliveryPrice ?? '0';

  const totalItems = getTotalOfItems(basketSummary);

  const products: OrderCompletedOnlineProducts[] = cart?.lineItems?.map(
    (item) => getOrderCompletedOnlineProduct(item)
  );

  const paymentMethod = isCencoPay
    ? totalSummary?.paymentMethod?.[0]?.bankCardType?.toLowerCase()
    : totalSummary?.paymentMethod?.[0]?.type;

  const hasFreeDelivery = checkFreeDelivery(totalDeliveryPrice);

  const hasRetiroEnTienda = hasPickUpInBaskets(basketSummary);

  const discountAmount = summary?.totalCouponPrice;
  const discountAsNumber = discountAmount ? formatToNumber(discountAmount) : 0;

  const giftcardAmount = summary?.totalPriceGiftCard;
  const giftcardAsNumber = giftcardAmount ? formatToNumber(giftcardAmount) : 0;

  const discountAmountFinal = discountAsNumber + giftcardAsNumber;

  const payload: OrderCompletedOnlinePayload = {
    total_order_price: totalOrderPrice,
    total_items: totalItems,
    fast_checkout: !!paymentSuccess?.isFastCheckout,
    channel: 'web',
    payment_type: paymentMethod ?? '',
    has_free_delivery: hasFreeDelivery,
    has_retiro_en_tienda: !!hasRetiroEnTienda,
    total_discounts: discountAmountFinal,
    products: products
  };

  return payload;
};
